import { useState, useMemo } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AutoAwesome,
  BugReport,
  Close,
  Inventory2,
  Search,
  Security,
  TrendingUp,
} from '@mui/icons-material';
import type { ChangeCategory } from '../../constants/changelog';
import { CHANGELOG } from '../../constants/changelog';
import { APP_NAME } from '../../constants/config';

interface ChangelogDialogProps {
  open: boolean;
  onClose: () => void;
}

const CATEGORY_CONFIG: Record<
  ChangeCategory,
  { label: string; color: string; icon: React.ReactNode }
> = {
  new: { label: 'New', color: '#4CAF50', icon: <AutoAwesome sx={{ fontSize: 14 }} /> },
  improved: { label: 'Improved', color: '#2196F3', icon: <TrendingUp sx={{ fontSize: 14 }} /> },
  fixed: { label: 'Fixed', color: '#FF9800', icon: <BugReport sx={{ fontSize: 14 }} /> },
  security: { label: 'Security', color: '#9C27B0', icon: <Security sx={{ fontSize: 14 }} /> },
  database: { label: 'Database', color: '#607D8B', icon: <Inventory2 sx={{ fontSize: 14 }} /> },
};

// Defensive fallback for an unrecognised category. `vite build` (the deploy
// path) does NOT typecheck, so a changelog entry with a bad `category` ships
// without error and used to crash the whole app — `CATEGORY_CONFIG[bad]` was
// undefined and `cfg.icon` threw at render, blanking the page. Never let a
// changelog typo take down the app again.
const FALLBACK_CONFIG = {
  label: 'Update',
  color: '#607D8B',
  icon: <AutoAwesome sx={{ fontSize: 14 }} />,
};

const ALL_CATEGORIES: ChangeCategory[] = ['new', 'improved', 'fixed', 'security', 'database'];

export const ChangelogDialog = ({ open, onClose }: ChangelogDialogProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<ChangeCategory>>(new Set());

  const toggleFilter = (cat: ChangeCategory) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const filteredChangelog = useMemo(() => {
    const query = searchValue.toLowerCase().trim();
    return CHANGELOG.map((version) => {
      const filtered = version.changes.filter((change) => {
        if (activeFilters.size > 0 && !activeFilters.has(change.category)) return false;
        if (query && !change.text.toLowerCase().includes(query)) return false;
        return true;
      });
      return { ...version, changes: filtered };
    }).filter((v) => v.changes.length > 0);
  }, [searchValue, activeFilters]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #3A4838 0%, #404C3E 40%, #4A5F45 100%)',
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2.5,
          px: 3,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.15rem' }}>
            What's New in {APP_NAME}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.25 }}>
            Release History & Changelog
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#FFFFFF' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Search */}
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search changelog..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.disabled', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: '#F5F7F3',
              },
            }}
          />
        </Box>

        {/* Filter chips */}
        <Box sx={{ px: 3, py: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {ALL_CATEGORIES.map((cat) => {
            const cfg = CATEGORY_CONFIG[cat];
            const isActive = activeFilters.has(cat);
            return (
              <Chip
                key={cat}
                icon={<Box sx={{ display: 'flex', color: 'inherit' }}>{cfg.icon}</Box>}
                label={cfg.label}
                size="small"
                variant={isActive ? 'filled' : 'outlined'}
                onClick={() => toggleFilter(cat)}
                sx={{
                  borderColor: cfg.color,
                  color: isActive ? '#FFFFFF' : cfg.color,
                  backgroundColor: isActive ? cfg.color : 'transparent',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  '&:hover': {
                    backgroundColor: isActive ? cfg.color : alpha(cfg.color, 0.08),
                  },
                  '& .MuiChip-icon': {
                    color: isActive ? '#FFFFFF' : cfg.color,
                  },
                }}
              />
            );
          })}
        </Box>

        {/* Version entries */}
        <Box sx={{ px: 3, pb: 3 }}>
          {filteredChangelog.length === 0 && (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}
            >
              No matching changes found
            </Typography>
          )}

          {filteredChangelog.map((version) => (
            <Box key={version.version} sx={{ mb: 3 }}>
              {/* Version header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 2,
                  pt: 1,
                }}
              >
                <Chip
                  label={version.version}
                  size="small"
                  sx={{
                    backgroundColor: '#4A6741',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    height: 26,
                  }}
                />
                {version.latest && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontWeight: 600 }}
                  >
                    Latest
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', ml: 'auto', fontSize: '0.8rem' }}
                >
                  {version.date}
                </Typography>
              </Box>

              {/* Change items */}
              {version.changes.map((change, idx) => {
                const cfg = CATEGORY_CONFIG[change.category] ?? FALLBACK_CONFIG;
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      mb: 1.5,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Chip
                      icon={
                        <Box sx={{ display: 'flex', color: 'inherit' }}>{cfg.icon}</Box>
                      }
                      label={cfg.label}
                      size="small"
                      sx={{
                        backgroundColor: alpha(cfg.color, 0.12),
                        color: cfg.color,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 24,
                        minWidth: 90,
                        flexShrink: 0,
                        mt: 0.25,
                        '& .MuiChip-icon': { color: cfg.color },
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        lineHeight: 1.6,
                        // Inline `<code>` tags get a subtle box.
                        '& code': {
                          fontFamily: 'monospace',
                          fontSize: '0.85em',
                          px: 0.5, py: 0.125,
                          borderRadius: 0.5,
                          backgroundColor: (theme) => theme.palette.action.hover,
                        },
                      }}
                      // Changelog strings are hardcoded in
                      // constants/changelog.ts (no user input), so
                      // rendering as HTML here is safe — and lets new
                      // entries use <b>, <code>, etc. for highlights.
                      // Existing pure-text entries render identically.
                      dangerouslySetInnerHTML={{ __html: change.text }}
                    />
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
